import React from 'react';
import { Helmet } from 'react-helmet';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Banner from '../Banner';
import FAQ, { FAQItem } from '../FAQ';
import { formatTitle, usableDescription } from '../PageMeta';
import Testimonial from '../Testimonial';
import References from '../References';
import BlogPostCard from '../BlogPostCard';
import { Link } from "react-router-dom";
import CourseCalendar from '../CourseCalendar';
import './style.scss';

const BlogPostsCarousel = ({ posts, header, style }) => {
  const [page, setPage] = React.useState(0);
  const [mobile, setMobile] = React.useState(false);
  const [numPages, setNumPages] = React.useState(0);
  const postsRef = React.createRef();

  function updateDimensions() {
    setMobile(window.innerWidth < 1024);
    setNumPages(window.innerWidth < 1024 ? posts.length : Math.ceil(posts.length / 3));
  }

  function scrollToPage(p) {
    if(p < 0) {
      p = 0;
    } else if(p >= numPages) {
      p = numPages - 1;
    }

    setPage(p);
    if (postsRef.current) {
      postsRef.current.scrollTo({
        left: postsRef.current.clientWidth * p,
        behavior: 'smooth'
      });
    }
  }

  React.useEffect(() => {
    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, [posts.length]);

  if (!posts || posts.length === 0) {
    return null;
  }

  let postElements;

  if(mobile) {
    postElements = posts.map((post, index) => {
      return (
        <div className="page" key={index}>
          <BlogPostCard post={{
            id: index,
            slug: post.slug,
            title: post.title,
            summary: post.summary,
            published: post.created_at,
            tags: post.tag_list,
            filler_image: post.filler_image
          }} />
        </div>
      );
    });
  } else {
    // Split posts into chunks of 3 per page for desktop
    let postPages = [];
    let chunk = [];

    posts.forEach((post, index) => {
      chunk.push(post);

      if(chunk.length === 3) {
        postPages.push(chunk);
        chunk = [];
      }
    });

    if (chunk.length > 0) {
      postPages.push(chunk);
    }
      
    postElements = postPages.map((page, pageIndex) => {
      return (
        <div className="page" key={pageIndex}>
          {page.map((post, index) => {
            return (
              <BlogPostCard 
                key={index} 
                post={{
                  id: `${pageIndex}-${index}`,
                  slug: post.slug,
                  title: post.title,
                  summary: post.summary,
                  published: post.created_at,
                  tags: post.tags,
                  filler_image: post.filler_image
                }} 
              />
            );
          })}
        </div>
      );
    });
  }

  return (
    <Container className="text-center" style={style}>
      <Row>
        <Col>
          {header && header.trim() !== '' && (
            <div className="text-center p-b-25">
              <h3 dangerouslySetInnerHTML={{ __html: header }} style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }} />
            </div>
          )}
          <div className="blog-posts-carousel">
            <div className={"arrow left" + (page === 0 ? ' disabled' : '')} onClick={() => {scrollToPage(page - 1)}}>&#10094;</div>
            <div className={"arrow right" + (page === numPages - 1 ? ' disabled' : '')} onClick={() => {scrollToPage(page + 1)}}>&#10095;</div>

            <div className="blog-posts" ref={postsRef}>
              {postElements}
            </div>

            <div className="blog-posts-carousel-dots">
              {(numPages > 0) && Array.from(Array(numPages).keys()).map((p, index) => {
                return <span className={"dot" + (page === p ? ' active' : '')} key={index} onClick={() => {scrollToPage(p)}}></span>
              })}
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

// Section names for breadcrumb structured data, keyed by first URL segment.
const SECTION_NAMES = {
  'turvallisuuspalvelut': 'Turvallisuuspalvelut',
  'vartiointi': 'Vartiointi',
  'jarjestyksenvalvonta': 'Järjestyksenvalvonta',
  'asiantuntijapalvelut': 'Asiantuntijapalvelut',
  'muutpalvelut': 'Muut palvelut',
  'koulutus': 'Koulutus',
  'tyonhakijalle': 'Työnhakijalle',
  'blogi': 'Blogi',
  'meista': 'Meistä',
  'yhteystiedot': 'Yhteystiedot'
};

const breadcrumbJsonLd = (pathname, pageTitle) => {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return null;
  const items = [{ '@type': 'ListItem', position: 1, name: 'Etusivu', item: 'https://www.nordicsec.fi/' }];
  let path = '';
  segments.forEach((segment, i) => {
    path += `/${segment}`;
    const isLast = i === segments.length - 1;
    const name = isLast ? pageTitle : (SECTION_NAMES[segment] || segment);
    items.push({ '@type': 'ListItem', position: i + 2, name, item: `https://www.nordicsec.fi${path}` });
  });
  return { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: items };
};

// Accepts "ff6600" or "#ff6600" from the CMS and returns a valid CSS colour or null.
const cssColor = (value) => {
  if (value === null || value === undefined) return null;
  const v = String(value).trim().replace(/^#/, '');
  return v ? `#${v}` : null;
};

const DynamicPage = ({ data }) => {
  const [ expandedQuestion, setExpandedQuestion] = React.useState('');

  if (!data || !data.page) {
    return <div>No page data available</div>;
  }

  const { page, content_blocks } = data;

  const toggleQuestion = (key) => {
    if (expandedQuestion === key) {
      setExpandedQuestion('');
    } else {
      setExpandedQuestion(key);
    }
  };

  const renderContentBlock = (block, index) => {
    let blockStyle = {
      paddingTop: `${block.top_margin || 0}px`,
      paddingBottom: `${block.bottom_margin || 0}px`
    };

    let blockWidthClasses;
    switch(block.width) {
    case '33':
      blockWidthClasses = 'col-sm-8 offset-sm-2';
      break;
    case '50':
      blockWidthClasses = 'col-sm-6 offset-sm-3';
      break;
    case '66':
      blockWidthClasses = 'col-sm-8 offset-sm-2';
      break;
    default:
      blockWidthClasses = 'col-sm-12';
    }

    let colorStyle = {};

    const backgroundColor = cssColor(block.background_color);
    const textColor = cssColor(block.text_color);
    if (backgroundColor) colorStyle.backgroundColor = backgroundColor;
    if (textColor) colorStyle.color = textColor;

    switch (block.content_type) {
      case 'basic': // Basic Content Block
        return (
          <div style={colorStyle}>
            <Container>
              <Row style={blockStyle}>
                <Col className={blockWidthClasses}>
                  <div 
                    key={block.id || index}
                  >
                    <div dangerouslySetInnerHTML={{ __html: block.content_html }} />
                  </div>
                </Col>
              </Row>
            </Container>
          </div>
        );
      
      case 'faq': // FAQ Component
        return (
          <div style={colorStyle}>
            <Container>
              <Row style={blockStyle}>
                <Col className={blockWidthClasses}>
                  <div key={block.id || index} style={blockStyle}>
                    <FAQ />
                  </div>
                </Col>
              </Row>
            </Container>
          </div>
        );
      
      case 'testimonials': // Testimonials Component
        return (
          <Container fluid style={colorStyle}>
            <Row style={blockStyle}>
              <Col>
                <div key={block.id || index}>
                  <Testimonial noButton={true} header={block.header} />
                </div>
              </Col>
            </Row>
          </Container>
        );
      
      case 'references': // References Component
        return (
          <Container fluid style={colorStyle}>
            <Row style={blockStyle}>
              <Col className={blockWidthClasses}>
                <div key={block.id || index}>
                  <References header={block.header} />
                </div>
              </Col>
            </Row>
          </Container>
        );
      
      case 'social': // Social Links Component
        return (
          <Container className="p-t-50 p-b-50">
            <Row>
              <Col sm={{ span: 8, offset: 2 }} className="text-center">
                <h3 className="p-b-50">Löydät meidät myös sosiaalisesta mediasta!</h3>

                <ul className="social-links">
                  <li><a href="https://www.facebook.com/NordicSec" target="_blank" rel="noopener noreferrer" className="facebook" aria-label="Nordic Security Alliance Facebookissa"><i className="fa fa-facebook" aria-hidden="true"></i></a></li>
                  <li><a href="https://www.instagram.com/nordicsecurityalliance/" target="_blank" rel="noopener noreferrer" className="instagram" aria-label="Nordic Security Alliance Instagramissa"><i className="fa fa-instagram" aria-hidden="true"></i></a></li>
                  <li><a href="https://www.linkedin.com/company/nordicsecurityalliance/" target="_blank" rel="noopener noreferrer" className="linkedin" aria-label="Nordic Security Alliance LinkedInissä"><i className="fa fa-linkedin" aria-hidden="true"></i></a></li>
                </ul>
              </Col>
            </Row>
          </Container>
        );
      
      case 'services': // Services Component
        return (
          <div key={block.id || index} style={colorStyle} className='servicesComponent'>
            <Container style={blockStyle}>
              {block.services && block.services
                .filter(service => service.image_url && service.image_url.trim() !== '')
                .map((service, serviceIndex) => (
                  <Row key={service.index} className={`p-t-100 ${serviceIndex === 0 ? 'first-service' : ''}`}>
                    <Col 
                      md={{span: 5, order: serviceIndex % 2 === 0 ? 1 : 2}} 
                      className={`${serviceIndex % 2 === 0 ? 'pr-md-4' : 'pl-md-4'} mb-4`}
                    >
                      <img src={service.image_url} alt={`Service ${service.index}`} className="img-fluid" />
                    </Col>
                    <Col md={{span: 7, order: serviceIndex % 2 === 0 ? 2 : 1}}>
                      <div dangerouslySetInnerHTML={{ __html: service.content_html }} />
                    </Col>
                  </Row>
                ))
              }
            </Container>
          </div>
        );

      case 'service_grid': // Service Grid Component
        return (
          <div style={colorStyle}>
          <Container key={block.id || index}>
            <Row style={blockStyle}>
              <Col sm={{ span: 8, offset: 2 }}>
                {block.header && block.header.trim() !== '' && (
                  <div className="text-center p-b-50">
                    <h3 dangerouslySetInnerHTML={{ __html: block.header }} />
                  </div>
                )}
                <div className="services-grid">
                  {block.services && block.services.map((service, serviceIndex) => (
                    <Link 
                      key={service.index || serviceIndex}
                      to={service.link || '#'}
                      className={`service-tile service-${service.index || serviceIndex + 1}`}
                      style={service.image_url ? {backgroundImage: `url(${service.image_url})`} : {}}
                    >
                      <span className="mobile" dangerouslySetInnerHTML={{ __html: service.text.replace(' ', '<br/>') }} />
                      <span className="desktop" dangerouslySetInnerHTML={{ __html: service.text }} />
                    </Link>
                  ))}
                </div>
              </Col>
            </Row>
          </Container>
          </div>
        );

      case 'blog_posts': // Blog Posts Carousel Component
        return (
          <div style={colorStyle} key={block.id || index}><BlogPostsCarousel  
            style={blockStyle}
            posts={block.blog_posts || []}
            header={block.header}
          /></div>
        );

      case 'inline_faq': // Inline FAQ Component
        return (
          <div style={colorStyle}>
            <Container key={block.id || index}>
              <Row>
                <Col style={blockStyle} className={blockWidthClasses}>
                  <section className="faq" aria-labelledby={`faq-${block.id || index}-heading`}>
                    {block.header && block.header.trim() !== '' ? (
                      <h2 id={`faq-${block.id || index}-heading`} className="faq__heading p-b-25" dangerouslySetInnerHTML={{ __html: block.header }}></h2>
                    ) : (
                      <h2 id={`faq-${block.id || index}-heading`} className="faq__heading p-b-25">Meiltä usein kysyttyä</h2>
                    )}

                    {block.questions && block.answers_html && block.questions.map((question, index2) => {
                      const key = `${block.id || index}-${index2}`;
                      return (
                        <FAQItem
                          key={key}
                          id={`faq-${key}`}
                          question={question}
                          questionIsHtml
                          answerHtml={block.answers_html[index2]}
                          expanded={expandedQuestion === key}
                          onToggle={() => toggleQuestion(key)}
                        />
                      );
                    })}
                  </section>
                </Col>
              </Row>
            </Container>
          </div>
        );

      case 'course_calendar': // Course Calendar Component
        return (
          <div style={colorStyle} key={block.id || index}>
            <Container style={blockStyle}>
              <Row>
                <Col className={blockWidthClasses}>
                  {block.header && block.header.trim() !== '' && (
                    <h3 className="p-b-25" dangerouslySetInnerHTML={{ __html: block.header }} />
                  )}
                  <CourseCalendar
                    filterCategory={block.filter_category}
                    filterCity={block.filter_city}
                    filterName={block.filter_name}
                    maxHeight={block.max_height}
                    mobileLimit={block.mobile_limit}
                  />
                </Col>
              </Row>
            </Container>
          </div>
        );

      case 'contact_row': // Contact Row Component
        return (
          <div style={colorStyle}>
            <Container key={block.id || index}>
              <Row style={blockStyle}>
                <Col className={blockWidthClasses}>
                  {block.header && block.header.trim() !== '' && (
                    <h2 dangerouslySetInnerHTML={{ __html: block.header }} />
                  )}
                  <Row className={`${block.contacts && block.contacts.length < 3 ? 'justify-content-center' : ''}`}>
                    {block.contacts && block.contacts
                      .sort((a, b) => (a.index || 0) - (b.index || 0))
                      .slice(0, 3)
                      .map((contact, contactIndex) => (
                        <Col lg={4} md={6} className="mb-4" key={contact.index || contactIndex}>
                          {contact.header && contact.header.trim() !== '' && (
                            <h4 className="text-center mb-3 contact-box-header" dangerouslySetInnerHTML={{ __html: contact.header}}></h4>
                          )}
                          <div className="contact-card">
                            {contact.image_url && (
                              <div className="contact-image mb-3 text-center">
                                <img src={contact.image_url} alt={contact.name || ''} className="img-fluid rounded-circle" style={{maxWidth: '150px', maxHeight: '150px'}} />
                              </div>
                            )}
                            {contact.name && (
                              <span><strong>{contact.name}</strong><br /></span>
                            )}
                            {contact.job_title && (
                              <span>{contact.job_title}<br /></span>
                            )}
                            {contact.phone && (
                              <span>
                                <a href={`tel:${contact.phone.replace(/\s/g, '').replace(/\(/g, '').replace(/\)/g, '')}`}>
                                  {contact.phone}
                                </a>
                                <br />
                              </span>
                            )}
                            {contact.email && (
                              <span>
                                <a href={`mailto:${contact.email}`}>{contact.email}</a>
                                <br />
                              </span>
                            )}
                            {contact.description_html && (
                              <span dangerouslySetInnerHTML={{ __html: contact.description_html }} />
                            )}
                          </div>
                        </Col>
                      ))
                    }
                  </Row>
                </Col>
              </Row>
            </Container>
          </div>
        );

      default:
        return (
          <Col className={blockWidthClasses}>
            <div key={block.id || index} style={blockStyle}>
              {block.content_type}
            </div>
          </Col>
        );
    }
  };

  // Titles carry soft hyphens for nav wrapping, as the &shy; entity or the raw
  // U+00AD character. Neither belongs in the <title> / og:title.
  const plainTitle = (page.title || '').replace(/&shy;/g, '').replace(/\u00AD/g, '');
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
  const fullTitle = formatTitle(plainTitle);
  const description = usableDescription(page.meta_description, plainTitle);
  const breadcrumbs = breadcrumbJsonLd(pathname, plainTitle);

  return (
    <div className="page dynamic">
      <Helmet>
        <title>{fullTitle}</title>
        <meta property="og:title" content={fullTitle} />
        {description && <meta name="description" content={description} />}
        {description && <meta property="og:description" content={description} />}
        {breadcrumbs && <script type="application/ld+json">{JSON.stringify(breadcrumbs)}</script>}
      </Helmet>
      <Banner image={page.hero_image_url}>
        <h1 dangerouslySetInnerHTML={{ __html: page.hero_title || page.title }}></h1>
      </Banner>

      {content_blocks && content_blocks.length > 0 ? (
        content_blocks
          .sort((a, b) => a.position - b.position)
          .map((block, index) => renderContentBlock(block, index))
      ) : (
        <p>No content blocks available</p>
      )}
    </div>
  );
};

export default DynamicPage;
